import { Accordion, AccordionDetails, AccordionSummary, Box, ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link } from "react-router-dom";
import { MenuGroup, MenuItem } from "../menuConfig";
import { menuIconMap } from "./menuIcons";
import { useActiveMenuRoute } from "./activeRoute";
import { activeGroupSx, menuRowSx } from "./menuStyles";

type Props = {
  group: MenuGroup;
  visibleItems: MenuItem[];
  onNavigate: () => void;
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
};

// Renders one sidebar section (Accordion) from data — replaces the old
// pattern of a hand-written component per section.
export const MenuAccordionSection = ({ group, visibleItems, onNavigate, expanded, onChange }: Props) => {
  const activeRoute = useActiveMenuRoute();
  const GroupIcon = menuIconMap[group.icon];
  const groupIsActive = visibleItems.some((item) => item.route === activeRoute);

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={onChange}
      sx={{ bgcolor: "transparent", "&::before": { display: "none" } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          ...menuRowSx,
          ...(groupIsActive ? activeGroupSx : {}),
          "& .MuiAccordionSummary-content": { m: 0, alignItems: "center" },
          "& .MuiAccordionSummary-expandIconWrapper": { color: "#94a3b8" },
          "& .MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root": { fontSize: 18 },
        }}
      >
        <ListItemIcon>
          <GroupIcon />
        </ListItemIcon>
        <ListItemText primary={group.label} slotProps={{ primary: { fontWeight: groupIsActive ? 600 : 500 } }} />
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, pb: 0.5 }}>
        {/* children indented under a thin guide line */}
        <Box sx={{ ml: 3.5, pl: 0.5, borderLeft: "1px solid #e5e7eb" }}>
          {visibleItems.map((item) => {
            const ItemIcon = menuIconMap[item.icon];
            return item.status === "placeholder" ? (
              <Tooltip key={item.label} title="Coming soon" placement="right">
                <span>
                  <ListItem disablePadding>
                    <ListItemButton disabled sx={menuRowSx}>
                      <ListItemIcon>
                        <ItemIcon />
                      </ListItemIcon>
                      <ListItemText primary={item.label} />
                    </ListItemButton>
                  </ListItem>
                </span>
              </Tooltip>
            ) : (
              <ListItem disablePadding key={item.label}>
                <ListItemButton
                  component={Link as React.ElementType}
                  to={item.route}
                  onClick={onNavigate}
                  selected={item.route === activeRoute}
                  sx={menuRowSx}
                >
                  <ListItemIcon>
                    <ItemIcon />
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
